import type { ExerciseClip } from "@repo/contract/catalog";
import {
  "ui-media.poster_alt" as mediaPosterAlt,
  "ui-media.video_label" as mediaVideoLabel,
} from "@repo/i18n";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { cx } from "../../lib/cx";
import { useSinglePlayer } from "./single-player-context";
import { thumbhashDataUrl } from "./thumbhash";
import { clipSrc, poster as posterUrl } from "./urls";
import { usePrefersReducedMotion } from "./use-reduced-motion";

/** What media needs of the contract's `exerciseClip`. */
export type MediaClip = Pick<
  ExerciseClip,
  "hash" | "thumbhash" | "width" | "height"
>;

export interface MediaProps {
  /**
   * The exercise's name. It becomes the poster's alt text and the clip's label;
   * `""` makes the media decorative, for a row that shows the name beside it.
   */
  alt: string;
  className?: string;
  clip: MediaClip;
  /**
   * Mount the looping clip and play it while it is on screen. Never in a list
   * row (plan D08): a row is a poster. Turning it off PAUSES; it never reloads.
   */
  play?: boolean;
  /** Above the fold: load the poster eagerly and at high priority. */
  priority?: boolean;
}

/**
 * An exercise's poster, and with `play` its clip, over the clip's ThumbHash.
 * Fills its box with `object-contain`, so the movement is never cropped.
 *
 * Keyed by hash: a different clip remounts the whole thing, which is the only
 * legitimate reload. A re-render with the same clip touches nothing — React
 * leaves an unchanged `src` attribute alone, and nothing here assigns
 * `video.src` by hand (T-155).
 */
export function Media(props: MediaProps) {
  return <MediaBody key={props.clip.hash} {...props} />;
}

// No destructuring defaults (`play = false`): React Compiler 1.0 bails out of
// the whole component on them ("lowerAssignment … AssignmentPattern").
function MediaBody({
  clip,
  alt,
  play: playProp,
  priority: priorityProp,
  className,
}: MediaProps) {
  const play = playProp ?? false;
  const priority = priorityProp ?? false;
  const reduced = usePrefersReducedMotion();
  const img = useRef<HTMLImageElement | null>(null);
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Once a clip has been asked to play it stays mounted, so `play` turning off
  // pauses it rather than throwing its buffer away. Reduced motion never
  // mounts it at all, and unmounts one that is running.
  const [armed, setArmed] = useState(play);
  if (play && !armed) {
    setArmed(true);
  }
  const showVideo = armed && !reduced;

  // A poster that fails (no poster for this hash, or offline) is hidden, so
  // the ThumbHash stays instead of a broken-image glyph. A listener, not an
  // `onError` prop: Biome refuses a handler on an `<img>` and an unstable one
  // on any element — and `load` is here for the same reason.
  useEffect(() => {
    const element = img.current;
    if (element === null) {
      return;
    }
    const fail = () => setFailed(true);
    const done = () => setLoaded(true);
    element.addEventListener("error", fail);
    element.addEventListener("load", done);
    // A poster already in the cache is decoded before this effect runs, and
    // its `load` has then fired with nothing listening. `complete` alone is
    // also true for an image that FAILED, so the pixels are checked.
    if (element.complete && element.naturalWidth > 0) {
      setLoaded(true);
    }
    return () => {
      element.removeEventListener("error", fail);
      element.removeEventListener("load", done);
    };
  }, []);

  // ── THE PLACEHOLDER COMES OFF WHEN THE POSTER IS PAINTED (T71) ────────────
  // A decoded ThumbHash has its own rounded aspect — 18×32 = 0.563 for Supino
  // inclinado, against that clip's 482/750 = 0.643 — so the wrapper's
  // `bg-contain` box does not coincide with the poster's `object-contain` box,
  // and the blur stuck out past the opaque white poster on one axis: grey
  // bands above and below a desktop plate, grey columns beside a deck card.
  // Kept while the poster is still coming, and kept for good when it fails,
  // because there is nothing else to show then.
  const placeholder =
    loaded && !failed ? null : thumbhashDataUrl(clip.thumbhash);
  const style: CSSProperties | undefined = placeholder
    ? { backgroundImage: `url(${placeholder})` }
    : undefined;

  const posterAlt = alt === "" ? "" : mediaPosterAlt({ name: alt });

  return (
    <span
      className={cx(
        "t-media relative block h-full w-full bg-center bg-contain bg-no-repeat",
        className
      )}
      style={style}
    >
      <img
        alt={showVideo ? "" : posterAlt}
        className={cx(
          "block h-full w-full object-contain",
          failed && "opacity-0"
        )}
        decoding="async"
        draggable={false}
        fetchPriority={priority ? "high" : "auto"}
        // The intrinsic size, so the browser knows the ratio before a byte
        // arrives; the classes size the box.
        height={clip.height}
        loading={priority ? "eager" : "lazy"}
        ref={img}
        src={posterUrl(clip.hash)}
        width={clip.width}
      />
      {showVideo ? (
        <Clip
          hash={clip.hash}
          label={alt === "" ? undefined : mediaVideoLabel({ name: alt })}
          play={play}
        />
      ) : null}
    </span>
  );
}

function Clip({
  hash,
  label,
  play,
}: {
  hash: string;
  label: string | undefined;
  play: boolean;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const player = useSinglePlayer();
  // Decided at mount and never again: the disk copy when the device holds
  // one (`D194`), else the CDN. A later answer must not touch `src` (T-155).
  const [src] = useState(() => clipSrc(hash));

  // Any start — the observer below, or anything else calling `play()` —
  // claims the single slot, so the video that was playing pauses.
  //
  // ── A `play` EVENT CAN ARRIVE AFTER ITS START WAS OVERRULED ───────────────
  // `play()` only QUEUES the event. When two clips start in the same moment
  // (two observers firing on mount), A starts, B's claim pauses A, B starts —
  // and only then does A's queued `play` run. Claiming there would pause B
  // for a start that no longer exists, and leave BOTH paused. A clip that is
  // already paused again when its event lands has lost; it claims nothing.
  useEffect(() => {
    const video = ref.current;
    if (video === null) {
      return;
    }
    const claim = () => {
      if (!video.paused) {
        player.claim(video);
      }
    };
    video.addEventListener("play", claim);
    return () => {
      video.removeEventListener("play", claim);
      player.release(video);
    };
  }, [player]);

  useEffect(() => {
    const video = ref.current;
    if (video === null) {
      return;
    }
    if (!play) {
      video.pause();
      return;
    }

    let inView = false;
    const sync = () => {
      if (inView && document.visibilityState === "visible") {
        player.claim(video);
        // Rejects when a pause lands before playback starts, or the clip
        // cannot load; the poster stays up either way.
        video.play().catch(() => undefined);
      } else {
        video.pause();
      }
    };
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          inView = entry.isIntersecting;
        }
        sync();
      },
      { threshold: 0.25 }
    );
    observer.observe(video);
    document.addEventListener("visibilitychange", sync);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      video.pause();
    };
  }, [play, player]);

  return (
    <video
      aria-hidden={label === undefined ? true : undefined}
      aria-label={label}
      className="absolute inset-0 block h-full w-full object-contain"
      disablePictureInPicture
      loop
      muted
      playsInline
      poster={posterUrl(hash)}
      preload="none"
      ref={ref}
      src={src}
    />
  );
}

export interface PlateProps extends MediaProps {
  /**
   * The inner inset. Default 12 % of the plate's SHORTER side. A thumbnail
   * passes a fixed one (`p-1`).
   */
  pad?: string;
}

/**
 * A white plate that never crops the movement: `--color-plate` in both
 * themes, because the posters are drawn on white, with A2's rim in dark
 * (`.a-plate`). Its aspect is the clip's own (`--ar` = width / height) unless
 * the caller sizes it (`size-12` sets both sides and the ratio yields).
 *
 * ── THE PADDING IS ON THE INNER SPAN, AND IN `cqmin` ───────────────────────
 * A percentage padding resolves against the containing block's WIDTH, top and
 * bottom included; on the plate itself it grew the box and, in A2, hid the
 * posters. On an absolutely-positioned inner span it cannot grow anything, and
 * `cqmin` against the plate's size container makes it 12 % of the shorter
 * side — a flat 12 % of the width would leave a 2.6-wide clip 38 % of its
 * height.
 */
export function Plate({ pad: padProp, className, ...media }: PlateProps) {
  const pad = padProp ?? "p-[12cqmin]";
  const style = {
    "--ar": media.clip.width / media.clip.height,
  } as CSSProperties;
  return (
    <span
      className={cx(
        "a-plate relative block aspect-(--ar) overflow-hidden bg-(--color-plate) [container-type:size]",
        className
      )}
      style={style}
    >
      <span className={cx("absolute inset-0 block", pad)}>
        <Media {...media} />
      </span>
    </span>
  );
}
